import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { interviewQuestions } from '../data/interviewQuestions';
import { Code, Search, ArrowRight } from 'lucide-react';

const DIFFICULTY_FILTERS = ['All', 'Easy', 'Medium', 'Hard'];

const extractTags = (summary) => {
    // Remove common introductory fluff
    let cleaned = summary.replace(/A classic question covering |Deep dive into |The infamous |Real-time /ig, '');
    // Split by comma or " and "
    let parts = cleaned.split(/, | and | with | using /i)
        .map(s => s.trim().replace(/\.$/, ''))
        .filter(s => s.length > 3 && s.length < 25);
    return parts.slice(0, 3);
};

const InterviewPrep = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [activeFilter, setActiveFilter] = useState('All');

    const filteredQuestions = interviewQuestions.filter(q => {
        const matchesSearch =
            q.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            q.summary.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesDifficulty = activeFilter === 'All' || q.difficulty === activeFilter;
        return matchesSearch && matchesDifficulty;
    });

    return (
        <>
            <Helmet>
                <title>Top System Design Interview Questions with Solutions | Rastor AI</title>
                <meta name="description" content="Practice the most-asked system design interview questions at Google, Amazon, Meta, Apple, Netflix, and Microsoft. Complete solutions with architecture diagrams." />
                <meta name="keywords" content="system design interview questions, FAANG system design, Google system design interview, design URL shortener, design chat application" />
            </Helmet>

            <div className="sd-container sd-fade-in">
                <div className="sd-interview-header sd-text-center sd-mb-5">
                    <div className="sd-badge sd-mb-2"><Code size={14} className="sd-inline-icon" /> Interview Prep</div>
                    <h1>Top System Design <span className="sd-text-gradient-secondary">Questions</span></h1>
                    <p className="sd-subtitle sd-mx-auto" style={{ maxWidth: '600px', margin: '0 auto' }}>
                        Real-world architectural problems with comprehensive, step-by-step solutions designed to help you ace your next big tech interview.
                    </p>
                </div>

                <div className="sd-search-bar sd-glass-card sd-mb-4">
                    <div className="sd-search-input-wrapper">
                        <Search className="sd-search-icon" size={20} />
                        <input
                            type="text"
                            placeholder="Search questions (e.g., URL Shortener, Chat App)..."
                            className="sd-search-input"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {/* Difficulty Filter Tabs */}
                <div className="sd-filter-tabs">
                    {DIFFICULTY_FILTERS.map(filter => (
                        <button
                            key={filter}
                            className={`sd-filter-tab ${activeFilter === filter ? 'active' : ''}`}
                            onClick={() => setActiveFilter(filter)}
                        >
                            {filter}
                        </button>
                    ))}
                </div>

                <div className="sd-result-count">
                    Showing <strong>{filteredQuestions.length}</strong> of <strong>{interviewQuestions.length}</strong> questions
                </div>

                <div className="sd-questions-grid sd-stagger">
                    {filteredQuestions.map((q) => (
                        <NavLink to={`/sysdesign/interview/${q.id}`} key={q.id} className="sd-question-card">
                            <div className="sd-question-header">
                                <h3>{q.title}</h3>
                                <span className={`sd-difficulty-badge ${q.difficulty.toLowerCase()}`}>
                                    {q.difficulty}
                                </span>
                            </div>
                            <p>{q.summary}</p>
                            <div className="sd-topic-tags">
                                {extractTags(q.summary).map(tag => (
                                    <span key={tag} className="sd-topic-tag">{tag}</span>
                                ))}
                            </div>
                            <div className="sd-read-more" style={{ marginTop: '0.75rem' }}>View Solution <ArrowRight size={16} /></div>
                        </NavLink>
                    ))}
                </div>
            </div>
        </>
    );
};

export default InterviewPrep;
